import { Discount } from "@happy-legal/graphql-types";
import { Box, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useContext, useEffect, useState } from "react";
import FormattedDateTimeTooltip from "../../../shared/components/FormattedDateTimeTooltip";
import SettingsSection from "../../../shared/components/SettingsSection";
import {
  ActionButton,
  TableActions,
  TableCell,
  TableRow,
} from "../../../shared/components/table/StyledTable";
import { PartitionKeyEnum, ProductTypeEnum } from "../../../shared/enums";
import { isSure } from "../../../shared/helpers";
import DiscountProvider, {
  DiscountContext,
} from "../../../shared/providers/DiscountProvider";
import {
  DELETE_DISCOUNT,
  GET_ALL_DISCOUNTS,
} from "../../../shared/reducer/discountReducer";
import { formatCurrency } from "../../../shared/utils";
import DiscountCodesDialog from "./DiscountCodesDialog";

/** Renders a list of enum-mapped values as stacked secondary text. */
const ValueList = ({ values }: { values: string[] }) => (
  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "grid" }}>
    {values.map((value) => (
      <Typography
        component="li"
        variant="body2"
        key={value}
        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
      >
        {value}
      </Typography>
    ))}
  </Box>
);

const DiscountCodes = () => (
  <DiscountProvider>
    <DiscountCodesConsumer />
  </DiscountProvider>
);

/**
 * Table displaying discount codes with CRUD abilities
 */
const DiscountCodesConsumer = () => {
  // Load state and reducer dispatch
  const { state, dispatch } = useContext(DiscountContext);

  // Load discount codes on render
  useEffect(() => {
    dispatch({ type: GET_ALL_DISCOUNTS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dialog/current id being edited state and handlers
  const [dialogOpen, setDialogOpen] = useState(false);
  const [id, setId] = useState<string>("");
  const handleOpenDialog = (nextId: string) => {
    setId(nextId);
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setId("");
    setDialogOpen(false);
  };

  // Discount code deletion fn
  const handleDelete = async (discountId: string) => {
    if (!(await isSure())) return;
    dispatch({
      type: DELETE_DISCOUNT,
      id: discountId,
    });
  };

  return (
    <>
      <SettingsSection
        title="Rabatt-Codes"
        description="Codes, Gültigkeit und betroffene Produkte"
        columns={[
          "ID",
          "Betrag",
          "Ablaufdatum",
          "Produkte",
          "Produkttypen",
          "Erlaubte Nutzungen",
          "Beschreibung",
        ]}
        onAdd={() => handleOpenDialog("")}
        addLabel="Rabatt-Code"
        isEmpty={!state?.length}
        emptyMessage="Zurzeit sind keine Rabatt-Codes im System vorhanden."
      >
        {state
          ?.slice()
          // Sort, with next to expire first
          ?.sort(
            (a: Discount, b: Discount) =>
              Number(new Date(a.expire)) - Number(new Date(b.expire)),
          )
          ?.map(
            ({
              allowedUses,
              amount,
              discountType,
              id: discountId,
              expire,
              products,
              productTypes,
              text,
            }: Discount) => (
              <TableRow key={discountId}>
                <TableCell sx={{ fontWeight: 600 }}>{discountId}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {discountType === "TOTAL"
                    ? formatCurrency(amount)
                    : `${amount} %`}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  <FormattedDateTimeTooltip date={expire} />
                </TableCell>
                <TableCell>
                  <ValueList
                    values={products.map((product) => PartitionKeyEnum[product])}
                  />
                </TableCell>
                <TableCell>
                  <ValueList
                    values={productTypes.map(
                      (productType) => ProductTypeEnum[productType],
                    )}
                  />
                </TableCell>
                <TableCell>{allowedUses === -1 ? "∞" : allowedUses}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{text}</TableCell>
                <TableCell align="right">
                  <TableActions>
                    <ActionButton
                      icon={<EditIcon />}
                      label="Rabatt-Code bearbeiten"
                      onClick={() => handleOpenDialog(discountId)}
                    />
                    <ActionButton
                      icon={<DeleteIcon />}
                      label="Rabatt-Code löschen"
                      onClick={() => handleDelete(discountId)}
                    />
                  </TableActions>
                </TableCell>
              </TableRow>
            ),
          )}
      </SettingsSection>

      {dialogOpen && (
        <DiscountCodesDialog id={id} handleCloseDialog={handleCloseDialog} />
      )}
    </>
  );
};

export default DiscountCodes;
